import { KebabHorizontalIcon, PencilIcon, PlusIcon, SyncIcon, TrashIcon } from '@primer/octicons-react';
import { ActionList, ActionMenu, Box, Checkbox, useConfirm } from '@primer/react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../components/ui/button';
import DataTable from '../../../components/ui/data-table';
import Text from '../../../components/ui/text';
import { useCommonContext } from '../../../contexts/common';
import { ePageBaseStatus } from '../../../models/ePageBaseStatus';
import { ILegalDocument } from '../../../models/response/legal-document/ILegalDocument';
import { actions } from '../../../state/actions/actionsWrapper';
import { RootState } from '../../../state/reducers';
import LegalDocumentEditModal from './LegalDocumentEditModal';

const LegalDocumentPage = () => {
    const { status, documents, isShowEditModal } = useSelector((x: RootState) => x.legalDocument.documents)
    const { groups } = useSelector((x: RootState) => x.legalDocument.group)
    const { translate } = useCommonContext();
    const confirm = useConfirm();
    const dispatch = useDispatch();
    useEffect(() => {
        if (status === ePageBaseStatus.is_not_initialization || status === ePageBaseStatus.is_need_reload) {
            dispatch(actions.legalDocument.document.LOAD_START(undefined))
        }
    }, [status])
    const handleDelete = async (id: number) => {
        if (await confirm({
            content: translate(`Base.Label.Confirm.Delete.Content`),
            title: translate(`Base.Label.Confirm.Delete.Title`),
            cancelButtonContent: translate(`Base.Label.Confirm.Delete.Cancel`),
            confirmButtonContent: translate(`Base.Label.Confirm.Delete.Confirm`),
            confirmButtonType: "danger",
        })) {
            dispatch(actions.legalDocument.document.DELETE_START(id))
        }
    }
    return (
        <Box sx={{ p: 3 }}>
            <DataTable
                title={translate(`LegalDocumentPage.Caption`)}
                data={documents.sort((a, b) => a.sis_legal_group_id - b.sis_legal_group_id)}
                columns={[
                    {
                        dataField: "sis_legal_group_id",
                        caption: translate(`LegalGroup.Name`),
                        // isMainColumn: true,
                        cellRender: (data: ILegalDocument) => {
                            const group = groups.find(x => x.id === data.sis_legal_group_id);
                            return (
                                <Box>
                                    <Box sx={{ fontWeight: 600 }}>{group?.name}</Box>
                                    <Box><i>{group?.name_en}</i></Box>
                                </Box>
                            );
                        }
                    },
                    {
                        dataField: "name",
                        caption: translate(`LegalDocument.Name`),
                        // isMainColumn: true,
                        cellRender: (data: ILegalDocument) => {
                            return (
                                <Box>
                                    <Box sx={{ fontWeight: 600 }}>{data.name}</Box>
                                    <Box><i>{data.name_en}</i></Box>
                                </Box>
                            );
                        }
                    },
                    {
                        dataField: "is_active",
                        align: "center",
                        width: "100px",
                        caption: translate(`LegalDocument.IsActive`),
                        cellRender: (data: ILegalDocument) => {
                            return <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <Checkbox checked={data.is_active} />
                            </Box>
                        }
                    },
                    {
                        id: "cmd",
                        align: "center",
                        width: "50px",
                        caption: translate(``),
                        cellRender: (data: ILegalDocument) => {
                            return <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <ActionMenu>
                                    <ActionMenu.Button icon={KebabHorizontalIcon} variant='invisible'>&nbsp;</ActionMenu.Button>
                                    <ActionMenu.Overlay>
                                        <ActionList>
                                            <ActionList.Item onSelect={() => {
                                                dispatch(actions.legalDocument.document.SHOW_EDIT_MODAL(data))
                                            }}>
                                                <ActionList.LeadingVisual><PencilIcon /></ActionList.LeadingVisual>
                                                <Text text='Base.Label.Edit' />
                                            </ActionList.Item>
                                            <ActionList.Divider />
                                            <ActionList.Item variant='danger' onSelect={() => {
                                                handleDelete(data.id)
                                            }}>
                                                <ActionList.LeadingVisual><TrashIcon /></ActionList.LeadingVisual>
                                                <Text text='Base.Label.Delete' />
                                            </ActionList.Item>
                                        </ActionList>
                                    </ActionMenu.Overlay>
                                </ActionMenu>
                            </Box>
                        }
                    },
                ]}
                searchEnable={false}
                isLoading={status === ePageBaseStatus.is_loading}
                actionComponent={
                    <Box sx={{ display: "flex" }}>
                        <Button text='Base.Label.Refresh'
                            size='medium'
                            leadingVisual={SyncIcon}
                            onClick={() => {
                                dispatch(actions.legalDocument.document.LOAD_START(undefined))
                            }}
                        />
                        <Button text='Base.Label.AddNew'
                            variant='primary'
                            size='medium'
                            leadingVisual={PlusIcon}
                            sx={{ ml: 1 }}
                            onClick={() => {
                                dispatch(actions.legalDocument.document.SHOW_EDIT_MODAL(undefined))
                            }}
                        />
                    </Box>
                }
            />
            {isShowEditModal &&
                <LegalDocumentEditModal />
            }
        </Box>
    );
};

export default LegalDocumentPage;